import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Building2, Plus, Users, User, X } from '../../icons';
import { Combobox } from '../../components/shared';
import { listAllTeams, listDivisions, type DivisionDto, type TeamDto } from '../../api/organization';
import { searchUsers } from '../../api/users';
import type { ShareTargetType, TargetInput } from '../../api/boards';
import { tBoards, getBoardsLang, subscribeBoardsLang } from '../../lib/boards-i18n';

/**
 * 공유·알림·열람자 대상 편집기 (migration 102).
 *
 * 게시판 설정의 "공유 부서 설정" · "알림 대상" 과 글쓰기의 "열람자 지정" 이 같은 모양이라
 * 한 부품으로 만든다. 서버 규약(workspace_shares 동형)을 그대로 쓴다 —
 * `targetType: company|division|team|user` + `targetId`(company 는 0).
 *
 * 팀을 사람 목록으로 펼쳐 저장하지 않는다. 멤버가 바뀌어도 대상 정의가 안 깨지도록
 * 발송·판정 시점에 서버가 편다.
 */

export interface TargetRow extends TargetInput {
  targetType: ShareTargetType;
  targetId?: string | null;
  canRead?: boolean;
  canNotify?: boolean;
}

interface TargetPickerProps {
  value: TargetRow[];
  onChange: (next: TargetRow[]) => void;
  /** 알림 체크박스를 노출할지. 열람자 지정에서는 필요 없다. */
  showNotify?: boolean;
  /**
   * 작성 허용 체크박스를 노출할지 (migration 105). 기본 꺼짐 —
   * 켜 주지 않으면 이 부품을 쓰는 다른 화면(옛 설정 모달·열람자 지정)은 종전과 똑같다.
   */
  showWrite?: boolean;
  disabled?: boolean;
}

const KIND_ICON: Record<ShareTargetType, typeof Users> = {
  company: Building2,
  division: Building2,
  team: Users,
  user: User,
};

function keyOf(t: TargetRow): string {
  return `${t.targetType}:${t.targetType === 'company' ? '0' : (t.targetId ?? '0')}`;
}

export function TargetPicker({
  value, onChange, showNotify = false, showWrite = false, disabled = false,
}: TargetPickerProps) {
  useSyncExternalStore(subscribeBoardsLang, getBoardsLang, getBoardsLang);
  const [kind, setKind] = useState<ShareTargetType>('team');
  const [divisions, setDivisions] = useState<DivisionDto[]>([]);
  const [teams, setTeams] = useState<Array<TeamDto & { divisionName: string | null }>>([]);
  const [userQuery, setUserQuery] = useState('');
  const [userOptions, setUserOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [pick, setPick] = useState<string>('');

  useEffect(() => {
    // stale-response race 봉합 — 코드베이스 표준 cancelled 플래그.
    let cancelled = false;
    Promise.all([listDivisions(), listAllTeams()])
      .then(([d, t]) => { if (!cancelled) { setDivisions(d); setTeams(t); } })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (kind !== 'user') return;
    const q = userQuery.trim();
    if (q.length === 0) { setUserOptions([]); return; }
    let cancelled = false;
    const handle = setTimeout(() => {
      searchUsers(q, 10)
        .then((rows) => { if (!cancelled) setUserOptions(rows.map((u) => ({ id: u.id, name: u.name }))); })
        .catch(() => undefined);
    }, 250);
    return () => { cancelled = true; clearTimeout(handle); };
  }, [kind, userQuery]);

  const nameOf = useCallback((t: TargetRow): string => {
    // 조직 목록 API 는 isActive=true 만 준다 — 삭제된 팀뿐 아니라 비활성된 팀도 여기서 못 찾는다.
    // 예전엔 raw id(`#22`)가 그대로 노출됐다.
    const gone = tBoards('boards.target.missing');
    if (t.targetType === 'company') return tBoards('boards.target.company');
    if (t.targetType === 'division') return divisions.find((d) => d.id === t.targetId)?.name ?? gone;
    if (t.targetType === 'team') return teams.find((x) => x.id === t.targetId)?.name ?? gone;
    return userOptions.find((u) => u.id === t.targetId)?.name ?? gone;
  }, [divisions, teams, userOptions]);

  const kindOptions = useMemo(() => ([
    { value: 'company', label: tBoards('boards.target.company') },
    { value: 'division', label: tBoards('boards.target.division') },
    { value: 'team', label: tBoards('boards.target.team') },
    { value: 'user', label: tBoards('boards.target.user') },
  ]), []);

  const pickOptions = useMemo(() => {
    if (kind === 'division') return divisions.map((d) => ({ value: d.id, label: d.name }));
    if (kind === 'team') return teams.map((t) => ({ value: t.id, label: t.divisionName ? `${t.divisionName} · ${t.name}` : t.name }));
    if (kind === 'user') return userOptions.map((u) => ({ value: u.id, label: u.name }));
    return [];
  }, [kind, divisions, teams, userOptions]);

  function add() {
    if (kind !== 'company' && !pick) return;
    const row: TargetRow = {
      targetType: kind,
      targetId: kind === 'company' ? '0' : pick,
      canRead: true,
      canNotify: false,
    };
    if (value.some((v) => keyOf(v) === keyOf(row))) return;
    onChange([...value, row]);
    setPick('');
    setUserQuery('');
  }

  return (
    <div className="bd-targets">
      <div className="bd-targets__add">
        <Combobox
          value={kind}
          onChange={(v) => { setKind(v as ShareTargetType); setPick(''); }}
          options={kindOptions}
          ariaLabel={tBoards('boards.target.pick')}
          className="bd-select"
          disabled={disabled}
        />
        {kind === 'user' && (
          <input
            className="bd-input"
            value={userQuery}
            disabled={disabled}
            placeholder={tBoards('boards.target.user')}
            aria-label={tBoards('boards.target.user')}
            onChange={(e) => setUserQuery(e.target.value)}
          />
        )}
        {kind !== 'company' && (
          <Combobox
            value={pick}
            onChange={setPick}
            options={pickOptions}
            searchable
            ariaLabel={tBoards('boards.target.pick')}
            placeholder={tBoards('boards.target.pick')}
            className="bd-select"
            disabled={disabled}
          />
        )}
        <button type="button" className="bd-btn bd-btn--ghost" onClick={add} disabled={disabled}>
          <Plus size={14} aria-hidden /> {tBoards('boards.btn.add')}
        </button>
      </div>

      {value.length === 0 ? (
        <p className="bd-muted">{tBoards('boards.target.empty')}</p>
      ) : (
        <ul className="bd-targets__list">
          {value.map((t) => {
            const Icon = KIND_ICON[t.targetType];
            const label = nameOf(t);
            return (
              <li key={keyOf(t)} className="bd-target">
                <Icon size={14} aria-hidden />
                <span className="bd-target__name">{label}</span>
                {showWrite && (
                  <label className="bd-target__notify">
                    <input
                      type="checkbox"
                      checked={t.canWrite ?? false}
                      disabled={disabled}
                      onChange={(e) => onChange(value.map((v) => (keyOf(v) === keyOf(t) ? { ...v, canWrite: e.target.checked } : v)))}
                    />
                    {tBoards('boards.target.can_write')}
                  </label>
                )}
                {showNotify && (
                  <label className="bd-target__notify">
                    <input
                      type="checkbox"
                      checked={t.canNotify ?? false}
                      disabled={disabled}
                      onChange={(e) => onChange(value.map((v) => (keyOf(v) === keyOf(t) ? { ...v, canNotify: e.target.checked } : v)))}
                    />
                    {tBoards('boards.target.can_notify')}
                  </label>
                )}
                <button
                  type="button"
                  className="bd-iconbtn"
                  disabled={disabled}
                  aria-label={tBoards('boards.target.remove', { name: label })}
                  onClick={() => onChange(value.filter((v) => keyOf(v) !== keyOf(t)))}
                >
                  <X size={13} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
